import { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Typography from "@mui/material/Typography";
import { confirmAlert } from "react-confirm-alert";
import { useDispatch, useSelector } from "react-redux";
import TurmaTableSelectRedux from "../Turma/TurmaTableSelectRedux";
import TurmaProfile from "../Turma/TurmaProfile";
import { setTurma } from "../Turma/actions/actions";
import { deleteById, findByTurmaCodigo, save as saveCronogramaData } from "./CronogramaData/api-cronograma-data";
import CronogramaDataTable from "./CronogramaData/CronogramaDataTable";
import CronogramaDataForm from "./CronogramaData/CronogramaDataForm";
import CronogramaDataProfile from "./CronogramaData/CronogramaDataProfile";
import CronogramaProfessorContainer from "./CronogramaProfessor/CronogramaProfessorContainer";
import CronogramaLocalContainer from "./CronogramaLocal/CronogramaLocalContainer";
import CronogramaTable from "./CronogramaTable";
import { errorMessage } from "../../util/ConfirmAlertUtil";
import { success } from "../../util/ToastUtil";
import Container from "../UI/Container";
import { mensagemDe, porDataInicio } from "./cronogramaUtil";

const TURMA = "turma";
const PROFESSOR = "professor";
const LOCAL = "local";

const CronogramaContainer = () => {
  const dispatch = useDispatch();
  const turma = useSelector((state) => state.turma);
  const [aba, setAba] = useState(TURMA);
  const [periodos, setPeriodos] = useState([]);
  const [isLoadingPeriodos, setIsLoadingPeriodos] = useState(true);
  const [periodoEditando, setPeriodoEditando] = useState(null);
  const [periodoSelecionado, setPeriodoSelecionado] = useState(null);
  const [isSavingPeriodo, setIsSavingPeriodo] = useState(false);
  const codTurma = turma?.codigo;

  useEffect(() => {
    setPeriodos([]);
    setPeriodoSelecionado(null);
    if (!codTurma) return undefined;
    let active = true;
    setIsLoadingPeriodos(true);
    findByTurmaCodigo(codTurma)
      .then((data) => {
        if (!active || !data) return;
        if (data.error) {
          errorMessage(mensagemDe(data));
        } else if (Array.isArray(data)) {
          setPeriodos([...data].sort(porDataInicio));
        }
      })
      .finally(() => {
        if (active) setIsLoadingPeriodos(false);
      });
    return () => {
      active = false;
    };
  }, [codTurma]);

  const handleSavePeriodo = (periodo) => {
    const eNovo = !periodo.codigo;
    setIsSavingPeriodo(true);
    saveCronogramaData({ ...periodo, turma })
      .then((data) => {
        if (!data || data.error) {
          errorMessage(mensagemDe(data, "Não foi possível salvar o período."));
          return;
        }
        setPeriodos((prevState) =>
          (eNovo ? [...prevState, data] : prevState.map((p) => (p.codigo === data.codigo ? data : p))).sort(porDataInicio),
        );
        setPeriodoEditando(null);
        success("Período salvo.");
      })
      .finally(() => setIsSavingPeriodo(false));
  };

  const handleDeletePeriodo = (periodo) => {
    confirmAlert({
      title: "Alerta",
      message: "Deseja remover este período do cronograma?",
      buttons: [
        {
          label: "Sim",
          onClick: () => {
            deleteById(periodo.codigo).then((data) => {
              if (data?.error) {
                errorMessage(mensagemDe(data));
                return;
              }
              setPeriodos((prevState) => prevState.filter((p) => p.codigo !== periodo.codigo));
              success("Período removido.");
            });
          },
        },
        { label: "Não", onClick: () => {} },
      ],
    });
  };

  const indiceSelecionado = periodoSelecionado
    ? periodos.findIndex((p) => p.codigo === periodoSelecionado.codigo)
    : -1;
  // Períodos ordenados por data de início: o anterior é o que vem antes do selecionado.
  const periodoAnterior = indiceSelecionado > 0 ? periodos[indiceSelecionado - 1] : null;

  const renderTurma = () => {
    if (!turma) return <TurmaTableSelectRedux />;
    return (
      <Stack spacing={3}>
        <Box className="no-print">
          <TurmaProfile turma={turma} handleTrocaTurma={() => dispatch(setTurma(null))} />
        </Box>
        {periodoSelecionado ? (
          <>
            <CronogramaDataProfile cronogramaData={periodoSelecionado} onTrocar={() => setPeriodoSelecionado(null)} />
            <CronogramaTable turma={turma} cronogramaData={periodoSelecionado} periodoAnterior={periodoAnterior} />
          </>
        ) : (
          <CronogramaDataTable
            listCronogramaData={periodos}
            isLoading={isLoadingPeriodos}
            onSelect={setPeriodoSelecionado}
            onEdit={setPeriodoEditando}
            onDelete={handleDeletePeriodo}
            onNovo={() => setPeriodoEditando({ eNovo: true })}
          />
        )}
      </Stack>
    );
  };

  return (
    <Container>
      <CronogramaDataForm
        cronogramaData={periodoEditando}
        onClose={() => setPeriodoEditando(null)}
        onSave={handleSavePeriodo}
        loadingSave={isSavingPeriodo}
      />

      <Box mb={1}>
        <Typography variant="h6">Cronograma</Typography>
        <Typography variant="body2" color="text.secondary" className="no-print">
          Quadro de aulas por turma, professor e local.
        </Typography>
      </Box>

      <Tabs
        value={aba}
        onChange={(event, value) => setAba(value)}
        className="no-print"
        sx={{ mb: 3, borderBottom: 1, borderColor: "divider" }}
      >
        <Tab value={TURMA} label="Turma" />
        <Tab value={PROFESSOR} label="Professor" />
        <Tab value={LOCAL} label="Local" />
      </Tabs>

      {aba === TURMA && renderTurma()}
      {aba === PROFESSOR && <CronogramaProfessorContainer />}
      {aba === LOCAL && <CronogramaLocalContainer />}
    </Container>
  );
};

export default CronogramaContainer;
